import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {useMemoizedSelector} from '../../../state/hooks';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {usePanelBankSearchResults} from '../search/PanelBankSearchResultsContext';
import {makePanelBankSearchSettingsSelector} from '../selectors';

const MAX_AUTO_OPEN_SECTIONS = 20;

type PanelBankSectionsOpenTempContextValue = {
  toggleIsOpenTemp: (sectionId: string) => void;
  openAllSectionsTemp: () => void;
  closeAllSectionsTemp: () => void;
  isSectionOpenWhileSearching: (
    sectionId: string,
    persistedValue: boolean
  ) => boolean;
};

const PanelBankSectionsOpenTempContext = createContext<
  PanelBankSectionsOpenTempContextValue | undefined
>(undefined);

type PanelBankSectionsOpenTempProviderProps = {
  children: React.ReactNode;
};

/**
 * This provides a way for sections to know if they are open during panel search.
 * Users can still toggle sections during search, and these changes will be saved
 * to the workspace spec. However, this is a temporary state that will be reset if
 * the search is cleared.
 */
export const PanelBankSectionsDuringSearchProvider = ({
  children,
}: PanelBankSectionsOpenTempProviderProps) => {
  const {panelBankActions, panelBankConfigRef} = useWorkspaceRefsContext();
  const {autoExpandSearchResults, searchSectionsOpen} = useMemoizedSelector(
    makePanelBankSearchSettingsSelector,
    panelBankConfigRef
  );
  const {visibleSections} = usePanelBankSearchResults();

  const autoExpandableSectionIds = useMemo(() => {
    const sectionIdsArr = visibleSections
      .slice(0, MAX_AUTO_OPEN_SECTIONS)
      .map(section => section.__id__);
    return new Set(sectionIdsArr);
  }, [visibleSections]);

  // Because there are workspaces with large amounts of sections (25k),
  // we don't want to store state for all sections by default. Instead
  // we track a baseline mode, and then the specific sections that
  // have been toggled to be different than all the rest.
  const mode =
    searchSectionsOpen?.mode ??
    (autoExpandSearchResults ? 'auto-expand' : 'default');
  const overrides = useMemo(
    () => new Set(searchSectionsOpen?.overrides ?? []),
    [searchSectionsOpen?.overrides]
  );

  const toggleIsOpenTemp = useCallback(
    (sectionId: string) => {
      const next = new Set(overrides);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      panelBankActions.updatePanelBankSettings({
        searchSectionsOpen: {
          mode,
          overrides: next.size ? [...next] : undefined,
        },
      });
    },
    [mode, overrides, panelBankActions]
  );

  const openAllSectionsTemp = useCallback(() => {
    panelBankActions.updatePanelBankSettings({
      searchSectionsOpen: {
        mode: 'expand-all',
        overrides: undefined,
      },
    });
  }, [panelBankActions]);
  const closeAllSectionsTemp = useCallback(() => {
    panelBankActions.updatePanelBankSettings({
      searchSectionsOpen: {
        mode: 'collapse-all',
        overrides: undefined,
      },
    });
  }, [panelBankActions]);

  const isSectionOpenWhileSearching = useCallback(
    (sectionId: string, persistedValue: boolean) => {
      let baselineValue = persistedValue;
      if (mode === 'auto-expand') {
        baselineValue = autoExpandableSectionIds.has(sectionId);
      } else if (mode === 'expand-all') {
        baselineValue = true;
      } else if (mode === 'collapse-all') {
        baselineValue = false;
      }
      const isOverridden = overrides.has(sectionId);
      return isOverridden ? !baselineValue : baselineValue;
    },
    [autoExpandableSectionIds, mode, overrides]
  );

  const value = useMemo(
    () => ({
      toggleIsOpenTemp,
      openAllSectionsTemp,
      closeAllSectionsTemp,
      isSectionOpenWhileSearching,
    }),
    [
      closeAllSectionsTemp,
      openAllSectionsTemp,
      isSectionOpenWhileSearching,
      toggleIsOpenTemp,
    ]
  );

  return (
    <PanelBankSectionsOpenTempContext.Provider value={value}>
      {children}
    </PanelBankSectionsOpenTempContext.Provider>
  );
};

export const usePanelBankSectionsOpenTempContext = () => {
  const value = useContext(PanelBankSectionsOpenTempContext);

  if (value == null) {
    throw new Error(
      'usePanelBankSectionsOpenTempContext must be used within a PanelBankSectionsOpenTempProvider'
    );
  }

  return value;
};
